import { useCallback, useState, type ReactNode } from 'react'

import { ToastContext, type ToastContextValue } from '@/components/ui/toastContext'

type ToastVariant = 'success' | 'error' | 'info'

interface ToastMessage {
  id: number
  variant: ToastVariant
  message: string
}

let nextId = 1

const VARIANT_CLASS: Record<ToastVariant, string> = {
  success: 'text-bg-success',
  error: 'text-bg-danger',
  info: 'text-bg-info',
}

const AUTO_DISMISS_MS = 5000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = nextId++
      setToasts((current) => [...current, { id, variant, message }])
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
    },
    [dismiss],
  )

  const value: ToastContextValue = {
    success: (message) => push('success', message),
    error: (message) => push('error', message),
    info: (message) => push('info', message),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast show align-items-center border-0 ${VARIANT_CLASS[toast.variant]}`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="d-flex">
              <div className="toast-body">{toast.message}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                aria-label="Close"
                onClick={() => dismiss(toast.id)}
              />
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
